import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Alert, 
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import temporaryNumberingService from '../services/temporaryNumberingService';

const TemporaryNumberingTestPage: React.FC = () => {
  const [itemType, setItemType] = useState('Panel');
  const [count, setCount] = useState(1);
  const [results, setResults] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const testGenerateTemporaryNumbers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await temporaryNumberingService.generateTemporaryNumbers({
        itemType,
        count: parseInt(count.toString())
      });
      
      const newResult = {
        id: Date.now(),
        type: 'generate',
        timestamp: new Date().toLocaleTimeString(),
        request: { itemType, count },
        response: response.data
      };
      
      setResults(prev => [newResult, ...prev]);
      console.log('Generated temporary numbers:', response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Error generating temporary numbers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const testRollbackNumbers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await temporaryNumberingService.rollbackTemporaryNumbers({
        itemType,
        count: parseInt(count.toString())
      });
      
      const newResult = {
        id: Date.now(),
        type: 'rollback',
        timestamp: new Date().toLocaleTimeString(),
        request: { itemType, count },
        response: response.data
      };
      
      setResults(prev => [newResult, ...prev]);
      console.log('Rollback successful:', response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Error rolling back numbers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const testFinalizeNumbers = async () => {
    if (results.length === 0) return;
    
    setIsLoading(true);
    setError(null);

    try {
      // Get the last generated numbers
      const lastGenerateResult = results.find(r => r.type === 'generate');
      if (!lastGenerateResult?.response?.temporary_item_numbers) {
        throw new Error('No generated numbers to finalize');
      }

      const response = await temporaryNumberingService.finalizeTemporaryNumbers(
        itemType,
        lastGenerateResult.response.temporary_item_numbers
      );
      
      const newResult = {
        id: Date.now(),
        type: 'finalize',
        timestamp: new Date().toLocaleTimeString(),
        request: { 
          itemType, 
          itemNumbers: lastGenerateResult.response.temporary_item_numbers 
        },
        response: response.data
      };
      
      setResults(prev => [newResult, ...prev]);
      console.log('Finalization successful:', response.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      console.error('Error finalizing numbers:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const clearResults = () => {
    setResults([]);
    setError(null);
  };

  const getItemTypeOptions = () => [
    'Panel', 'Frame', 'Panel Window', 'Net Fixing'
  ];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        🧪 Temporary Numbering Test
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Test the temporary numbering system for order creation with auto mode.
      </Typography>

      <Grid container spacing={3}>
        {/* Control Panel */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Controls
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                select
                label="Item Type"
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
                size="small"
                SelectProps={{
                  native: true,
                }}
              >
                {getItemTypeOptions().map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </TextField>
              
              <TextField
                label="Count"
                type="number"
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                size="small"
                inputProps={{ min: 1, max: 10 }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                onClick={testGenerateTemporaryNumbers}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? <CircularProgress size={20} /> : 'Generate Numbers'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={testRollbackNumbers}
                disabled={isLoading}
                fullWidth
              >
                {isLoading ? <CircularProgress size={20} /> : 'Rollback Numbers'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={testFinalizeNumbers}
                disabled={isLoading || results.length === 0}
                fullWidth
              >
                {isLoading ? <CircularProgress size={20} /> : 'Finalize Numbers'}
              </Button>
              
              <Button
                variant="text"
                onClick={clearResults}
                disabled={isLoading}
                fullWidth
              >
                Clear Results
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Test Results
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {results.length === 0 ? (
              <Typography color="text.secondary">
                No test results yet. Click "Generate Numbers" to start testing.
              </Typography>
            ) : (
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {results.map((result, index) => (
                  <Card key={result.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ 
                          color: result.type === 'generate' ? 'success.main' : 
                                result.type === 'rollback' ? 'warning.main' : 'info.main',
                          fontWeight: 'bold'
                        }}>
                          {result.type === 'generate' ? '🔄 Generated' : 
                           result.type === 'rollback' ? '↩️ Rolled Back' : '✅ Finalized'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.timestamp}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Request:</strong> {result.request.itemType} × {result.request.count}
                      </Typography>
                      
                      {result.response.temporary_item_numbers && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Generated Numbers:</strong> 
                        <Box component="span" sx={{ 
                          ml: 1, 
                          fontFamily: 'monospace', 
                          bgcolor: 'success.light', 
                          px: 1, 
                          py: 0.5, 
                          borderRadius: 0.5,
                          color: 'success.dark',
                          fontWeight: 'bold'
                        }}>
                          {result.response.temporary_item_numbers.join(', ')}
                        </Box>
                      </Typography>
                      )}
                      
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>ICM Count:</strong> {result.response.icm_count}
                      </Typography>
                      
                      <Typography variant="body2" sx={{ 
                        fontFamily: 'monospace', 
                        bgcolor: 'grey.100', 
                        p: 1, 
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(result.response, null, 2)}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemporaryNumberingTestPage;
